import moment = require("moment");
import React = require("react");
import {Duration} from "moment";
import {Types} from "../../types/Types";
import {TimelineNavigator, TimelinePeriods} from "./timetools/TimelineNavigator";
import "./Toolbar.scss";
import {ToolbarContextInterface, ToolbarContextProvider} from "./ToolbarContext";
import {Tool, ToolsSelector} from "./ToolsSelector";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../../index";
import AutoSizer from "react-virtualized-auto-sizer";

interface ToolbarProps {
    enabled: boolean;
    toolsAvailable: Tool[];
    granularities: Granularity[];
    onBrush: (period: Date[]) => any;
    retrieveData: (granularity: number) => void;
    timelineExtent: Types.Period;
    timelineMaxExtent: Types.Period;
    timelineSelectedPeriod?: Types.Period;
    texts: any;
    loadingExcel?: boolean;
}

export enum GranularityType {
    YEAR,
    QUARTER,
    MONTH,
    WEEK,
    DAY,
    HOUR,
}

export interface RangeLimit {
    min: Duration;
    max: Duration;
}

export interface Granularity {
    key: GranularityType;
    name: string;
    available: boolean;
    rangeLimits: RangeLimit;
    timeInterval: d3.TimeInterval;
}

export interface PredefinedPeriod {
    key: any;
    name: string;
    available: boolean;
    duration: Duration;
}

interface ToolbarState {
    enabled: boolean;
    toolSelectedKey: any;
    timelineExtent: Types.Period;
    timelineSelectedPeriod: Types.Period;
    timelineMaxExtent: Types.Period;
    granularitySelected: GranularityType;
    granularities: Granularity[];
    hasBrushEnded: boolean;
    excelLoading?: boolean;
}

export class Toolbar extends React.Component<ToolbarProps, ToolbarState> {
    private static logEnabled: boolean = false;

    constructor(props: ToolbarProps) {
        super(props);
        this.state = {
            enabled: this.props.enabled,
            toolSelectedKey: props.toolsAvailable[0].key,
            timelineExtent: this.props.timelineExtent,
            timelineMaxExtent: this.props.timelineMaxExtent,
            timelineSelectedPeriod: (this.props.timelineSelectedPeriod === undefined
                ? this.props.timelineExtent
                : this.props.timelineSelectedPeriod),
            granularitySelected: GranularityType.DAY,
            granularities: this.props.granularities,
            hasBrushEnded: false,
        };
        this.onBrush = this.onBrush.bind(this);
        this.retrieveData = this.retrieveData.bind(this);
        this.handleToolSelectedChange = this.handleToolSelectedChange.bind(this);
        this.handleBrushEnd = this.handleBrushEnd.bind(this);
        this.handleTimelineSelectedPeriodChange = this.handleTimelineSelectedPeriodChange.bind(this);
        this.handleTimelinePeriodsChange = this.handleTimelinePeriodsChange.bind(this);
        this.handleTimelineExtentStartChange = this.handleTimelineExtentStartChange.bind(this);
        this.handleTimelineExtentEndChange = this.handleTimelineExtentEndChange.bind(this);
        this.handleTimelineExtentChange = this.handleTimelineExtentChange.bind(this);
        this.canMoveExtentBack = this.canMoveExtentBack.bind(this);
        this.canMoveExtentForward = this.canMoveExtentForward.bind(this);
        this.handleGranularitySelectedChange = this.handleGranularitySelectedChange.bind(this);
        this.handleMoveExtent = this.handleMoveExtent.bind(this);
    }

    public render() {
        const toolbarContextValue: ToolbarContextInterface = {
            toolSelectedKey: this.state.toolSelectedKey,
            timelineExtent: this.state.timelineExtent,
            timelineSelectedPeriod: this.state.timelineSelectedPeriod,
            timelineMaxExtent: this.state.timelineMaxExtent,
            granularitySelected: this.state.granularitySelected,
            granularities: this.state.granularities,
            handleToolSelectedChange: this.handleToolSelectedChange,
            handleTimelineSelectedPeriodChange: this.handleTimelineSelectedPeriodChange,
            handleTimelineExtentChange: this.handleTimelineExtentChange,
            handleTimelineExtentStartChange: this.handleTimelineExtentStartChange,
            handleTimelineExtentEndChange: this.handleTimelineExtentEndChange,
            handleMoveExtent: this.handleMoveExtent,
            handleBrushEnd: this.handleBrushEnd,
            handleGranularitySelectedChange: this.handleGranularitySelectedChange,
            canMoveExtentBack: this.canMoveExtentBack(),
            canMoveExtentForward: this.canMoveExtentForward(),
            getGranularitySelected: () => {
                return this.state.granularities.filter((g) => g.key === this.state.granularitySelected).pop();
            },
            getPrettyExtentDuration: this.getPrettyExtentDuration,

        };

        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <ToolbarContextProvider value={toolbarContextValue}>
                    <AutoSizer disableHeight style={{width: "100%", height: "100%"}}>
                        {({width}) => <div className={"toolbar__content"}>
                            <ToolsSelector
                                enabled={this.state.enabled}
                                toolsAvailable={this.props.toolsAvailable}
                                activeToolKey={toolbarContextValue.toolSelectedKey}
                                width={width * 0.20}
                                excelLoading={this.props.loadingExcel}
                            />
                            <TimelineNavigator
                                enabled={this.state.enabled}
                                key={"timelineNavigator"}
                                width={width * 0.80}
                            />
                        </div>}
                    </AutoSizer>
                </ToolbarContextProvider>
            </ThemeProvider>
        );
    }

    /**
     * update toolbar state agree with the new tool selected and update elements connected to the toolbar
     */
    private handleToolSelectedChange(newValue: any) {
        if (newValue === "export") { // FIXME: define export as costant
            // in this case the selection of this tool cannot be persisted on state. It is a one time action tool
            const temporaryTool = this.props.toolsAvailable.filter((t) => t.key === newValue)
                .pop();
            temporaryTool.onClick();
            return;
        }
        this.setState({toolSelectedKey: newValue}, () => {
            this.log("State Tool selector: " + this.state.toolSelectedKey);
            const currentTool = this.props.toolsAvailable.filter((t) => t.key === this.state.toolSelectedKey)
                .pop();
            currentTool.onClick();
        });
    }

    private handleBrushEnd(newPeriod: Types.Period) {
        const granularitySelected = this.state.granularities
            .filter((g) => g.key === this.state.granularitySelected).pop();
        newPeriod = TimelineNavigator.adjustPeriod(newPeriod, granularitySelected);
        if (this.state.timelineSelectedPeriod.start.toDate().getTime() === newPeriod.start.toDate().getTime() &&
            this.state.timelineSelectedPeriod.end.toDate().getTime() === newPeriod.end.toDate().getTime()) {
            return;
        }

        const timelinePeriods = TimelineNavigator.validateTimeline(
            this.state.timelineExtent,
            granularitySelected,
            newPeriod,
            this.state.timelineMaxExtent,
            this.state.timelineMaxExtent,
        );

        this.handleTimelinePeriodsChange({
            currentMaxExtent: this.state.timelineMaxExtent,
            currentExtent: timelinePeriods.currentExtent,
            selectedPeriod: timelinePeriods.selectedPeriod,
        }, true);
    }

    /*
     * Given new selected period, update toolbar elements agree with them and update elements connected to the toolbar
     */
    private handleTimelineSelectedPeriodChange(newPeriod: Types.Period) {
        if (this.state.timelineSelectedPeriod.start.toDate().getTime() === newPeriod.start.toDate().getTime() &&
            this.state.timelineSelectedPeriod.end.toDate().getTime() === newPeriod.end.toDate().getTime()) {
            return;
        }
        const newGranularities = this.getGranularities(newPeriod);
        this.setState({
                timelineMaxExtent: this.state.timelineMaxExtent,
                timelineExtent: this.state.timelineExtent,
                timelineSelectedPeriod: newPeriod,
                granularities: this.getGranularities(newPeriod),
                granularitySelected: this.getGranularityKeyToSelect(newGranularities),
                hasBrushEnded: false,
            },
            this.onBrush);
    }

    /*
     * Given new timeline periods, update toolbar elements agree with them
     */
    private handleTimelinePeriodsChange(timelinePeriods: TimelinePeriods, hasBrushEnded: boolean) {
        const newGranularities = this.getGranularities(timelinePeriods.selectedPeriod);
        let callback = this.onBrush;
        const newGranularitySelected = this.getGranularityKeyToSelect(newGranularities);
        if (!this.state.enabled) {
            callback = undefined;
        } else if (this.state.timelineExtent.start.valueOf() !== timelinePeriods.currentExtent.start.valueOf()
            || this.state.timelineExtent.end.valueOf() !== timelinePeriods.currentExtent.end.valueOf()
            || this.state.granularitySelected !== newGranularitySelected) {
            callback = this.retrieveData;
        }
        this.setState({
            enabled: true,
            timelineMaxExtent: timelinePeriods.currentMaxExtent,
            timelineExtent: timelinePeriods.currentExtent,
            timelineSelectedPeriod: timelinePeriods.selectedPeriod,
            granularities: newGranularities,
            granularitySelected: this.getGranularityKeyToSelect(newGranularities),
            hasBrushEnded,
        }, callback);
    }

    /*
     * Given a new timeline extent, update toolbar elements agree with it and update data
     */
    private handleTimelineExtentChange(newExtent: Date[]) {
        const newTimelineExtent: Types.Period = {
            start: moment(newExtent[0]),
            end: moment(newExtent[1]),
        };
        const newGranularities = this.getGranularities(newTimelineExtent);
        this.setState({
            timelineExtent: newTimelineExtent,
            timelineSelectedPeriod: newTimelineExtent,
            granularities: this.getGranularities(newTimelineExtent),
            granularitySelected: this.getGranularityKeyToSelect(newGranularities),
        }, this.retrieveData);
    }

    /*
     * Given a new start extent date, update toolbar elements agree with it and update data
     */
    private handleTimelineExtentStartChange(newStart: Date) {
        const newTimelineExtent: Types.Period = {start: moment(newStart.getTime()), end: this.state.timelineExtent.end};
        const newGranularities = this.getGranularities(newTimelineExtent);
        const granularityToSelect = this.getGranularityKeyToSelect(newGranularities);
        const timelinePeriods = TimelineNavigator.validateTimeline(
            newTimelineExtent,
            newGranularities.filter((g) => g.key === granularityToSelect).pop(),
            newTimelineExtent,
            this.state.timelineMaxExtent,
            this.state.timelineMaxExtent,
        );
        this.setState({
            timelineMaxExtent: timelinePeriods.currentMaxExtent,
            timelineExtent: timelinePeriods.currentExtent,
            timelineSelectedPeriod: timelinePeriods.selectedPeriod,
            granularities: newGranularities,
            granularitySelected: granularityToSelect,
        }, this.retrieveData);
    }

    /*
     * Given a new end extent date, update toolbar elements agree with it and update data
     */
    private handleTimelineExtentEndChange(newEnd: Date) {
        const newTimelineExtent: Types.Period = {start: this.state.timelineExtent.start, end: moment(newEnd.getTime())};
        const newGranularities = this.getGranularities(newTimelineExtent);
        const granularityToSelect = this.getGranularityKeyToSelect(newGranularities);

        const timelinePeriods = TimelineNavigator.validateTimeline(
            newTimelineExtent,
            newGranularities.filter((g) => g.key === granularityToSelect).pop(),
            newTimelineExtent,
            this.state.timelineMaxExtent,
            this.state.timelineMaxExtent,
        );
        this.setState({
            timelineMaxExtent: timelinePeriods.currentMaxExtent,
            timelineExtent: timelinePeriods.currentExtent,
            timelineSelectedPeriod: timelinePeriods.currentExtent,
            granularities: newGranularities,
            granularitySelected: granularityToSelect,
        }, this.retrieveData);
    }

    /*
     * return true if the timeline extent can be moved backward
     */
    private canMoveExtentBack(): boolean {
        if (this.state.timelineExtent === undefined) {
            return false;
        }
        const currentExtentDuration: Duration = moment.duration(
            this.state.timelineExtent.end.diff(this.state.timelineExtent.start, "millisecond", true),
            "millisecond",
        );
        return !this.state.timelineExtent.start.clone()
            .subtract(currentExtentDuration)
            .isBefore(this.state.timelineMaxExtent.start);
    }

    /*
     * return true if the timeline extent can be moved forward
     */
    private canMoveExtentForward(): boolean {
        if (this.state.timelineExtent === undefined) {
            return false;
        }
        const currentExtentDuration: Duration = moment.duration(
            this.state.timelineExtent.end.diff(this.state.timelineExtent.start, "millisecond", true),
            "millisecond",
        );
        return !this.state.timelineExtent.end.clone()
            .add(currentExtentDuration)
            .isAfter(this.state.timelineMaxExtent.end);
    }

    /**
     * Manage the granularity change and update chart connected to the toolbar
     */
    private handleGranularitySelectedChange(
        newGranularitySelected: GranularityType,
        newExtent: Types.Period,
        newSelectedPeriod: Types.Period) {
        this.setState(
            {
                granularitySelected: newGranularitySelected,
                timelineExtent: newExtent,
                timelineSelectedPeriod: newSelectedPeriod,
            }, this.retrieveData);
    }

    /*
     * Given a direction of movement: prev | next adjust the toolbar timeline extent moving it about a quantity
     * equals to the current extent duration
     */
    private handleMoveExtent(direction: string) {
        let newExtent;
        const currentExtentDuration: Duration = moment.duration(
            this.state.timelineExtent.end.diff(this.state.timelineExtent.start, "millisecond", true),
            "millisecond",
        );
        if (direction === "prev") {
            newExtent = {
                start: this.state.timelineExtent.start.clone().subtract(currentExtentDuration),
                end: this.state.timelineExtent.end.clone().subtract(currentExtentDuration),
            };
        } else {
            newExtent = {
                start: this.state.timelineExtent.start.clone().add(currentExtentDuration),
                end: this.state.timelineExtent.end.clone().add(currentExtentDuration),
            };
        }
        this.handleTimelineExtentChange([newExtent.start.toDate(), newExtent.end.toDate()]);
    }

    /**
     *  Return true if the given granularity is available for the given period
     */
    private isGranularityAvailableFor(granularity: Granularity, period: Types.Period) {
        const periodDuration = moment.duration(period.end.diff(period.start)).asMilliseconds();
        const isAvailable = (periodDuration >= granularity.rangeLimits.min.asMilliseconds() &&
            periodDuration <= granularity.rangeLimits.max.asMilliseconds());
        return isAvailable;
    }

    /*
     * Return the set of granularities setted for the given period
     */
    private getGranularities(period: Types.Period): Granularity[] {
        const newGranularities: Granularity[] = [];
        this.state.granularities.map((g) => {
            const newG = g;
            g.available = this.isGranularityAvailableFor(g, period);
            newGranularities.push(g);
        });
        return newGranularities;
    }

    /**
     * Given a set of granularities return the key of the granularity to automatically select, agree with
     * how many granularities are available and the current one selected
     */
    private getGranularityKeyToSelect = (granularities: Granularity[]): any => {
        const availableGranularities = granularities.filter((g) => g.available);
        if (availableGranularities.length === 1) {
            return availableGranularities.pop().key;
        }
        const availableGranularitiesKey = availableGranularities.map((g) => g.key).sort();

        if (availableGranularitiesKey.filter((gk) => gk === this.state.granularitySelected).length !== 0) {
            return this.state.granularitySelected;
        } else if (availableGranularitiesKey.filter((gk) => gk > this.state.granularitySelected).length === 0) {
            return availableGranularitiesKey.pop();
        } else {
            return availableGranularitiesKey.reverse().pop();
        }
    }

    /**
     * Return the current extent duration representation in the current granularity unit of measure
     */
    private getPrettyExtentDuration = (): string => {
        const extentDuration: Duration = moment.duration(
            this.state.timelineExtent.end.diff(this.state.timelineExtent.start),
        );
        switch (this.state.granularitySelected) {
            case GranularityType.YEAR:
                return extentDuration.asYears().toFixed(0) + " " + this.props.texts.granularity_yearly_unit;
            case GranularityType.QUARTER:
                return extentDuration.asMonths().toFixed(0) + " " + this.props.texts.granularity_monthly_unit;
            case GranularityType.MONTH:
                return extentDuration.asMonths().toFixed(0) + " " + this.props.texts.granularity_monthly_unit;
            case GranularityType.WEEK:
                return extentDuration.asWeeks().toFixed(0) + " " + this.props.texts.granularity_weekly_unit;
            case GranularityType.DAY:
                return extentDuration.asDays().toFixed(0) + " " + this.props.texts.granularity_daily_unit;
            case GranularityType.HOUR:
                return extentDuration.asHours().toFixed(0) + " " + this.props.texts.granularity_hourly_unit;
        }
        return "";
    }

    private retrieveData() {
        this.props.retrieveData(this.state.granularitySelected);
    }

    private onBrush() {
        const brushSelectedPeriod: Date[] = [
            this.state.timelineSelectedPeriod.start.toDate(),
            this.state.timelineSelectedPeriod.end.toDate()];
        return this.props.onBrush(brushSelectedPeriod);
    }

    private log(message: any): void {
        if (Toolbar.logEnabled) {
            console.log(message);
        }
    }
}
